import { MarketplaceIcon } from '@components/ConnectedMarketplaces';
import { useAuth } from '@contexts/auth';
import { Flag } from '@elements/Flag';
import { DarkPopup } from '@elements/Popup';
import { metadataHook } from '@hooks/metadata';
import { CommonUtility } from '@utility/common';
import { tooltipTexts } from '@utility/constants/common';
import { Permissions } from '@utility/constants/permission';
import { Plus } from '@phosphor-icons/react';
import { cn } from '@skupreme/shared/ui';
import type { ReactNode } from 'react';

const ROW = 'sk:flex sk:w-full sk:flex-wrap sk:justify-center sk:gap-[15px]';

const TILE = cn(
  'sk:mx-3 sk:my-2 sk:flex sk:items-center sk:rounded-card sk:bg-surface sk:px-4 sk:py-2',
  'sk:inset-shadow-hairline sk:shadow-card sk:transition-colors sk:duration-75',
);

const TILE_ENABLED = cn(
  TILE,
  'sk:cursor-pointer sk:appearance-none sk:border-0 sk:font-sans sk:text-fg',
  'sk:hover:bg-fill-quaternary',
  'sk:outline-none sk:focus-visible:outline-2 sk:focus-visible:outline-solid sk:focus-visible:outline-offset-1 sk:focus-visible:outline-primary',
);

const TILE_DISABLED = cn(
  TILE,
  'sk:cursor-not-allowed sk:text-fg-tertiary sk:opacity-60 sk:grayscale',
);

/* The marketplace icons are drawn for this 50px slot. */
const ICON_SLOT =
  'sk:ml-4 sk:flex sk:h-12.5 sk:items-center sk:justify-center sk:rounded-[5px] sk:py-[5px]';

/* Wayfair's icon carries its own padding and needs a wider, lower slot. */
const WAYFAIR_ICON_SLOT =
  'sk:ml-2 sk:py-0 sk:[&_img]:m-0 sk:[&_img]:mb-[5px] sk:[&_img]:w-[45px] sk:[&_img]:p-0';

function AddTile({
  disabled = false,
  onClick,
  children,
}: {
  disabled?: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  if (disabled) {
    return (
      <div aria-disabled className={TILE_DISABLED}>
        <Plus size={13} weight="thin" />
        {children}
      </div>
    );
  }

  return (
    <button type="button" className={TILE_ENABLED} onClick={onClick}>
      <Plus size={13} weight="thin" />
      {children}
    </button>
  );
}

type ConnectedMarketplace = {
  marketplace_id: string;
  type: string;
  alias?: string;
  countries_code: string[];
};

export function ConnectedList({ data }: { data: ConnectedMarketplace[] }) {
  return (
    <div className={ROW}>
      {data.map(item => (
        <div
          key={item.marketplace_id}
          className="sk:relative sk:flex sk:h-full sk:max-w-[220px] sk:flex-col sk:justify-around sk:rounded-card sk:bg-surface sk:px-4 sk:py-6 sk:inset-shadow-hairline sk:shadow-card"
        >
          <div className="sk:flex sk:max-h-25 sk:items-center sk:justify-center sk:py-4">
            <img
              src={`/images/integrations/${item.type.toLowerCase()}.png`}
              alt={item.type}
              className="sk:h-auto sk:max-h-12.5 sk:max-w-full"
            />
          </div>
          <div className="sk:m-0 sk:flex sk:flex-wrap sk:items-center sk:justify-center sk:text-fg-secondary">
            {item.countries_code.map(x => (
              <Flag
                title={x}
                className="sk:mr-1"
                key={x}
                name={x.toLowerCase()}
              />
            ))}
            <span className="sk:mt-2 sk:mb-2 sk:text-center sk:text-sm sk:leading-[1.4]">
              {item.alias}
            </span>
          </div>
        </div>
      ))}
    </div>
  );
}

export function IntegrationList({
  data,
  marketplaceClick,
  permission,
  wrappedInRow = true,
}) {
  const { metadata } = metadataHook();
  const { isSuperAdmin, isOwner } = useAuth();

  const disabled = !(
    isOwner ||
    isSuperAdmin ||
    [Permissions.admin, Permissions.editor].includes(permission)
  );

  const mapped = data
    .filter(market => {
      const { type, marketplace_connected } = market;
      const marketplaceData =
        // @ts-expect-error - TS2339: Property 'marketplaces' does not exist on type '{} | IMetadata'.
        type && metadata?.marketplaces?.[type?.toLowerCase()];

      if (!marketplace_connected && marketplaceData?.hidden === true) {
        return false;
      }

      return true;
    })
    .map(market => {
      const { type, marketplace_id } = market;
      const marketplaceData =
        type &&
        // @ts-expect-error - TS2339: Property 'marketplaces' does not exist on type '{} | IMetadata'.
        metadata?.marketplaces?.[type.toLowerCase()];

      if (!CommonUtility.isValidObject(marketplaceData)) {
        return null;
      }

      const details = (
        <AddTile
          key={marketplace_id}
          disabled={disabled}
          onClick={() => marketplaceClick(market)}
        >
          <div
            title={type}
            className={cn(
              ICON_SLOT,
              market?.name === 'wayfair_us' && WAYFAIR_ICON_SLOT,
            )}
          >
            {/* @ts-expect-error - TS2739: Type '{ marketplace: any; }' is missing the following proper... */}
            <MarketplaceIcon marketplace={market} />
          </div>
        </AddTile>
      );

      if (disabled) {
        return (
          <DarkPopup trigger={details} key={marketplace_id}>
            {tooltipTexts.integrationDisabled}
          </DarkPopup>
        );
      }

      return details;
    });

  if (!wrappedInRow) {
    return mapped;
  }

  return <div className={ROW}>{mapped}</div>;
}

type InactiveAd = {
  marketplace_utils_id: string;
  imgSrcInActive?: string;
};

export function AdsIntegrationList({
  inActiveAds,
  onClick = () => {},
}: {
  inActiveAds: InactiveAd[];
  onClick?: (ad: InactiveAd) => void;
}) {
  return (
    <>
      {inActiveAds.map(ad => (
        <AddTile key={ad.marketplace_utils_id} onClick={() => onClick(ad)}>
          <div className={ICON_SLOT}>
            <img
              src={ad?.imgSrcInActive}
              alt=""
              className="sk:size-[36.67px] sk:p-[5px]"
            />
          </div>
        </AddTile>
      ))}
    </>
  );
}
